import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { firstValueFrom, take } from 'rxjs';
import { CotacaoDTO } from '../../models/gerenciar-contas/cotacao.model';
import { EmpresaConfigService } from '../configuracoes/empresa.service';
import { CotacaoService } from './cotacao.service';
import { CotacaoDialogService } from './cotacao-dialog.service';

export interface GarantirCotacaoOpts {
  /** Moeda da cotacao a buscar. Default: moedaEmpresa, depois moeda da sessao, depois GUARANI. */
  moeda?: string | null;
  /** Empresa dona da cotacao (ex.: cliente na Area do Contador). */
  empresaId?: number | null;
  /** Moeda padrao da empresa dona da cotacao (repassada ao dialogo). */
  moedaEmpresa?: string | null;
  /** Exige pesoGuarani > 0. Default: true quando a moeda for PESO. */
  exigirPeso?: boolean;
  /** Se nao houver cotacao da moeda, aceita a cotacao GUARANI (comportamento do PDV). Default: false. */
  fallbackGuarani?: boolean;
  /** Nao exibe o aviso quando o usuario cancela o dialogo. */
  silencioso?: boolean;
}

/**
 * Garante que existe cotacao do dia antes de finalizar uma operacao.
 * Quando nao existe, abre o dialogo global de cotacao e aguarda o usuario
 * cadastrar. Retorna a cotacao (com id) ou null se o usuario cancelou.
 */
@Injectable({ providedIn: 'root' })
export class CotacaoGuardService {
  /** Evita dois dialogos simultaneos: chamadas concorrentes dividem a mesma promessa. */
  private pendente: Promise<CotacaoDTO | null> | null = null;

  constructor(
    private cotacaoService: CotacaoService,
    private dialogService: CotacaoDialogService,
    private empresaService: EmpresaConfigService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  garantirCotacao(opts: GarantirCotacaoOpts = {}): Promise<CotacaoDTO | null> {
    if (this.pendente) {
      return this.pendente;
    }
    const p = this.executar(opts).finally(() => {
      this.pendente = null;
    });
    this.pendente = p;
    return p;
  }

  private async executar(opts: GarantirCotacaoOpts): Promise<CotacaoDTO | null> {
    const moeda = await this.resolverMoeda(opts);

    const existente = await this.buscar(moeda, opts);
    if (existente) {
      return existente;
    }

    // Sem cotacao valida: solicita ao usuario.
    const salvou = await this.solicitar(moeda, opts);
    if (!salvou) {
      if (!opts.silencioso) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('common.atencao'),
          detail: this.translate.instant('cotacao_dialog.necessaria'),
        });
      }
      return null;
    }

    this.cotacaoService.clearCotacaoCache();
    const nova = await this.buscar(moeda, opts);
    if (!nova && !opts.silencioso) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.translate.instant('cotacao_dialog.necessaria'),
      });
    }
    return nova;
  }

  private async resolverMoeda(opts: GarantirCotacaoOpts): Promise<string> {
    if (opts.moeda) return opts.moeda;
    if (opts.moedaEmpresa) return opts.moedaEmpresa;
    try {
      const cfg: any = await firstValueFrom(this.empresaService.getSessao());
      const enumStr = cfg?.moeda?.enum;
      if (enumStr) return enumStr;
    } catch {
      /* usa GUARANI */
    }
    return 'GUARANI';
  }

  /** Busca a cotacao de hoje; null se ausente (404) ou invalida (PESO sem pesoGuarani). */
  private async buscar(moeda: string, opts: GarantirCotacaoOpts): Promise<CotacaoDTO | null> {
    const exigirPeso = opts.exigirPeso ?? moeda === 'PESO';
    const valida = (c: CotacaoDTO | null | undefined): c is CotacaoDTO =>
      !!c && !!c.id && (!exigirPeso || (c.pesoGuarani != null && c.pesoGuarani > 0));

    try {
      const c = await firstValueFrom(this.cotacaoService.getCotacaoAtual(moeda));
      if (valida(c)) return c;
    } catch {
      /* ausente: tenta fallback / solicita */
    }
    if (opts.fallbackGuarani && moeda !== 'GUARANI') {
      try {
        const c = await firstValueFrom(this.cotacaoService.getCotacaoAtual('GUARANI'));
        if (valida(c)) return c;
      } catch {
        /* sem cotacao */
      }
    }
    return null;
  }

  /** Abre o dialogo global e aguarda; true se salvou, false se cancelou. */
  private solicitar(moeda: string, opts: GarantirCotacaoOpts): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const subSalva = this.dialogService.cotacaoSalva$.pipe(take(1)).subscribe(() => {
        subCancel.unsubscribe();
        resolve(true);
      });
      const subCancel = this.dialogService.cotacaoCancelada$.pipe(take(1)).subscribe(() => {
        subSalva.unsubscribe();
        resolve(false);
      });
      this.dialogService.abrirDialog({
        empresaId: opts.empresaId ?? null,
        moedaEmpresa: opts.moedaEmpresa ?? moeda,
        subtituloChave: 'cotacao_dialog.subtitulo_nao_cadastrada',
      });
    });
  }
}
